import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchApi } from "@/lib/api";
import { CLERK_AVAILABLE, getDemoAuthHeaders, getClerkAuthHeaders } from "@/lib/auth";
import { useAuthConditional } from "@/lib/use-auth-conditional";

interface Invitation {
  id: string;
  email: string;
  role: "owner" | "contributor";
  status: "pending" | "accepted" | "expired" | "revoked";
  invitedAt: string;
  expiresAt: string;
  acceptedAt: string | null;
  invitedBy: {
    id: string;
    name: string;
  } | null;
}

interface CreateInvitationResponse {
  invitation: Invitation;
  inviteUrl: string;
  project: {
    id: string;
    name: string;
  };
}

interface InvitationsResponse {
  invitations: Invitation[];
}

// Fetch invitations for a project
export function useInvitations(projectId: string) {
  const auth = useAuthConditional();

  return useQuery({
    queryKey: ["invitations", projectId],
    queryFn: async (): Promise<Invitation[]> => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();
      const response = await fetchApi<InvitationsResponse>(
        `/api/projects/${projectId}/invitations`,
        { headers }
      );
      return response.invitations;
    },
    enabled: auth.isSignedIn && !!projectId,
  });
}

// Create a new invitation
export function useCreateInvitation(projectId: string) {
  const queryClient = useQueryClient();
  const auth = useAuthConditional();

  return useMutation({
    mutationFn: async (data: { email: string; role: "owner" | "contributor" }) => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();
      const response = await fetchApi<CreateInvitationResponse>(
        `/api/projects/${projectId}/invitations`,
        {
          method: "POST",
          headers,
          body: JSON.stringify(data),
        }
      );
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invitations", projectId] });
    },
  });
}

// Revoke an invitation
export function useRevokeInvitation(projectId: string) {
  const queryClient = useQueryClient();
  const auth = useAuthConditional();

  return useMutation({
    mutationFn: async (invitationId: string) => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();
      await fetchApi(`/api/projects/${projectId}/invitations/${invitationId}`, {
        method: "DELETE",
        headers,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invitations", projectId] });
    },
  });
}

// Get invitation by token (public - for accept flow)
export function useInvitationByToken(token: string) {
  return useQuery({
    queryKey: ["invitation", token],
    queryFn: async () => {
      const response = await fetchApi<{
        invitation: {
          id: string;
          email: string;
          role: string;
          status: string;
          expiresAt: string;
        };
        project: {
          id: string;
          name: string;
        };
        invitedBy: {
          name: string;
        } | null;
      }>(`/api/invitations/${token}`);
      return response;
    },
    enabled: !!token,
    retry: false,
  });
}

// Accept an invitation
export function useAcceptInvitation() {
  const auth = useAuthConditional();

  return useMutation({
    mutationFn: async (token: string) => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();
      const response = await fetchApi<{
        message: string;
        project: { id: string; name: string };
        role: string;
      }>(`/api/invitations/${token}/accept`, {
        method: "POST",
        headers,
        body: JSON.stringify({}),
      });
      return response;
    },
  });
}
